<template>
  <div>
    <b-form novalidate @submit.prevent="submit">
      <b-tabs v-model="step" nav-class="mb-1" card pills>
        <b-tab title="Step 1: Subscription Details" :active="step === 0">
          <div v-if="validationErrors.length" class="alert alert-danger py-2 px-3 mb-3">
            <div v-for="(error, index) in validationErrors" :key="index">
              {{ error }}
            </div>
          </div>
          <b-form-group label="Subscription Name">
            <b-form-input v-model="form.name" />
          </b-form-group>
          <b-form-group label="Description">
            <b-form-textarea v-model="form.description" rows="3" max-rows="6" />
          </b-form-group>
          <b-form-group label="Send Login Details?">
            <b-form-checkbox v-model="form.send_login" />
          </b-form-group>
          <b-form-group label="Redirect URL (on success)">
            <b-form-input ref="redirectUrlInput" v-model="form.redirect_url" type="text" />
          </b-form-group>
          <b-form-group label="Payment Type">
            <b-form-radio-group v-model="form.mode" :options="[
              { text: 'One-Time Payment', value: 'one_time' },
              { text: 'Recurring Subscription', value: 'subscription' },
            ]" :disabled="!!form.id" />
          </b-form-group>
          <b-form-group v-if="form.mode === 'subscription'" label="Billing Interval">
            <b-form-select v-model="form.interval" :options="['month', 'year']" :disabled="!!form.id" />
          </b-form-group>
          <b-form-group label="Amount (USD)">
            <b-form-input v-model.number="form.amount" type="number" min="0.5" step="0.01" :disabled="!!form.id" />
          </b-form-group>
          <b-form-group label="Stripe API Key">
            <b-input-group>
              <b-form-input
                :type="showStripeKey ? 'text' : 'text'"
                :value="showStripeKey ? form.stripe_api_key : maskedStripeKey"
                @input="onStripeKeyInput"
              />
              <b-input-group-append>
                <b-button variant="outline-secondary" size="sm" @click="toggleStripeKey">
                  <span v-if="showStripeKey">Hide</span>
                  <span v-else>Show</span>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <b-button variant="primary" class="mt-3 float-right" @click="nextStep">Next</b-button>
        </b-tab>
        <b-tab title="Step 2: Modules to Enable" :active="step === 1">
          <div v-if="validationErrors.length" class="alert alert-danger py-2 px-3 mb-3">
            <div v-for="(error, index) in validationErrors" :key="index">
              {{ error }}
            </div>
          </div>
          <div>
            <b-form-group>
              <div
                v-for="mod in availableModules"
                :key="mod.key"
                class="form-check mb-3"
              >
                <b-form-checkbox
                  v-model="form.modules"
                  :value="mod.key"
                  class="form-check-input"
                >
                  {{ mod.label }}
                </b-form-checkbox>
              </div>
            </b-form-group>
          </div>
          <b-button variant="secondary" class="mt-3" @click="prevStep">Back</b-button>
          <b-button variant="primary" class="mt-3 float-right" @click="nextStep">Next</b-button>
        </b-tab>
        <b-tab title="Step 3: Business Limits" :active="step === 2">
          <div v-if="validationErrors.length" class="alert alert-danger py-2 px-3 mb-3">
            <div v-for="(error, index) in validationErrors" :key="index">
              {{ error }}
            </div>
          </div>
          <div class="alert alert-info py-2 px-3 mb-3">
            Limits are optional. Set <strong>0</strong> for unlimited usage.
          </div>
          <div>
            <div v-for="limit in visibleLimits" :key="limit.key" class="mb-3 text-black">
              <b-form-group :label="limit.label">
                <b-form-input
                  v-model.number="form.module_limits[limit.key]"
                  type="number"
                  min="0"
                  class="form-control"
                  data-lpignore="true"
                  autocomplete="off"
                />
              </b-form-group>
            </div>
          </div>
          <b-button variant="secondary" class="mt-3" @click="prevStep">Back</b-button>
          <b-button type="submit" variant="primary" :disabled="loading" class="mt-3 float-right">
            <span v-if="loading">
              <b-spinner small type="grow" class="mr-2" /> Saving...
            </span>
            <span v-else>
              Save Subscription
            </span>
          </b-button>
        </b-tab>
      </b-tabs>
    </b-form>
  </div>
</template>
<style scoped>
.card-header-dark-blue >>> .card-header {
  display: none;
}
.card-header {
  background-color: unset !important;
  padding: 0;
}
:deep(.card-header) {
  background-color: unset !important;
  padding: 0 10px;
}
.b-tab .b-card,
.b-tab > div {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.b-tabs .nav-tabs {
  margin-bottom: 0 !important;
}
.b-tabs .tab-pane,
.b-tabs .card-body,
.b-tabs-card > .tab-content > .tab-pane,
.b-tabs-card > .tab-content,
.tab-content,
.tab-pane,
.card-body {
  padding: 0 !important;
}
div[data-lastpass-icon-root]{
    display: none;
  }
</style>
<script>
export default {
  props: {
    formData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      step: 0,
      loading: false,
      validationErrors: [],
      form: {
        id: null,
        name: '',
        description: '',
        send_login: true,
        redirect_url: '',
        module_limits: {
          reply_engine_reply_limit:0,
          review_surge_email_limit:0,
          review_surge_text_limit:0,
          social_fusion_post_limit:0,
          social_reviews_post_limit:0,
          tap_engine_employee_limit:0,
          team_user_limit:0
        },
        mode: 'subscription',
        interval: 'month',
        amount: null,
        modules: [],
        agency_id: null,
        stripe_api_key: ''
      },
      limitDefinitions: [
        {
          key: 'reply_engine_reply_limit',
          label: 'ReplyEngine: Monthly Reply Limit',
          condition: () => this.user?.has_re,
        },
        {
          key: 'tap_engine_employee_limit',
          label: 'TapEngine: Employee Limit',
          condition: () => this.user?.has_te,
        },
        {
          key: 'social_reviews_post_limit',
          label: 'SocialReviews: Monthly Post Limit',
          condition: () => this.user?.has_social_review,
        },
        {
          key: 'social_fusion_post_limit',
          label: 'SocialFusion: Monthly Post Limit',
          condition: () => this.user?.has_social_fusion,
        },
        {
          key: 'review_surge_text_limit',
          label: 'ReviewSurge: Monthly Text Message Limit',
          condition: () => this.user?.has_review_requester,
        },
        {
          key: 'review_surge_email_limit',
          label: 'ReviewSurge: Monthly Email Limit',
          condition: () => this.user?.has_review_requester,
        },
        {
          key: 'team_user_limit',
          label: 'User Limit',
          condition: () => true,
        },
      ],
      allModules: [
        { key: 'has_re', label: 'AI Review Reply' },
        { key: 'has_te', label: 'NFC Reviews' },
        { key: 'has_social_review', label: 'Social Review Posting' },
        { key: 'has_social_fusion', label: 'AI Social Media Posts' },
        { key: 'has_review_requester', label: 'Email/SMS Review Requests' },
        { key: 'has_insta_stream', label: 'Instagram Feed' },
        { key: 'has_lead_machine', label: 'Review Widgets' }
      ],
      showStripeKey: false
    };
  },
  computed: {
    visibleLimits() {
      return this.limitDefinitions.filter(limit => limit.condition());
    },
    user() {
      return this.$store.getters['auth/user']
    },
    availableModules() {
      return this.allModules.filter(mod => this.user[mod.key]);
    },
    agency_id(){
      return this.user?.agency?.id;
    },
    maskedStripeKey() {
      const key = this.form.stripe_api_key;
      if (key.length <= 4) return key; // Don't mask if key is too short
      return `${key.slice(0, 4)}****${key.slice(-4)}`;
    }
  },
  created() {
    // Initialize all limit keys to 0
    this.limitDefinitions.forEach(def => {
      this.$set(this.form.module_limits, def.key, 0);
    });

  },
  mounted() {
    if (this.formData) {
      this.form = { ...this.formData };
      // Prefill Stripe API key from agency settings if not set in formData
      if (!this.form.stripe_api_key && this.user?.agency?.settings.stripe_api_key) {
        this.form.stripe_api_key = this.user.agency.settings.stripe_api_key;
      }
    } else {
      // Create form: prefill Stripe API Key from agency settings if available
      if (this.user?.agency?.settings.stripe_api_key) {
        this.form.stripe_api_key = this.user.agency.settings.stripe_api_key;
      }
    }
    this.form.agency_id = this.agency_id;
  },
  methods: {
    validateAll() {
      const errors = [];
      // Step 1 validations
      if (!this.form.name) {
        errors.push({ msg: 'Subscription Name is required.', step: 0, ref: null });
      }
      if (!this.form.amount) {
        errors.push({ msg: 'Amount is required.', step: 0, ref: null });
      }
      if (this.form.mode === 'subscription' && !this.form.interval) {
        errors.push({ msg: 'Interval is required for subscriptions.', step: 0, ref: null });
      }
      // Redirect URL validation
      if (this.form.redirect_url && !this.isValidUrl(this.form.redirect_url)) {
        errors.push({ msg: `Redirect URL is not valid. Please enter a full URL starting with https:// (e.g., https://example.com). You entered: ${this.form.redirect_url}`, step: 0, ref: 'redirectUrlInput' });
      }
      // Step 2 validations
      if (!this.form.modules || this.form.modules.length === 0) {
        errors.push({ msg: 'At least one module must be selected.', step: 1, ref: null });
      }
      // Step 3 validations
      this.visibleLimits.forEach(limit => {
        if (this.form.module_limits[limit.key] == null || this.form.module_limits[limit.key] === '') {
          errors.push({ msg: `${limit.label} is required.`, step: 2, ref: null });
        }
      });
      this.validationErrors = errors.map(e => e.msg);
      if (errors.length > 0) {
        // Go to the first error's tab and focus if ref is set
        const first = errors[0];
        this.step = first.step;
        if (first.ref) {
          this.$nextTick(() => {
            if (this.$refs[first.ref]) {
              this.$refs[first.ref].focus();
            }
          });
        }
        return false;
      }
      return true;
    },
    isValidUrl(url) {
      try {
        void new URL(url);
        return true;
      } catch (e) {
        return false;
      }
    },
    validateStep() {
      const errors = [];
      if (this.step === 0) {
        if (!this.form.name) {
          errors.push('Subscription Name is required.');
        }
        if (!this.form.amount) {
          errors.push('Amount is required.');
        }
        if (this.form.mode === 'subscription') {
          if (!this.form.interval) {
            errors.push('Interval is required for subscriptions.');
          }
        }
      }
      // Add more step-specific validations here if needed
      this.validationErrors = errors;
      return errors.length === 0;
    },
    nextStep() {
      if (this.validateStep()) {
        if (this.step < 2) this.step++;
      }
    },
    async submit() {
      if (!this.validateAll()) return;
      this.loading = true;
      this.$store
        .dispatch('agency/submitSubscriptionForm', this.form)
        .then((resp) => {
          this.$emit('created', resp);
          this.loading = false;
        })
        .catch((err) => {
          if (err?.response?.data?.errors) {
            this.$refs.updateLandingForm.setErrors(err.response.data.errors)
          }
          this.loading = false;
        })
    },
    prevStep() {
      if (this.step > 0) this.step--;
    },
    toggleStripeKey() {
      this.showStripeKey = !this.showStripeKey;
    },
    onStripeKeyInput(value) {
      this.$set(this.form, 'stripe_api_key', value);
      // Optionally, you can auto-toggle the show/hide if the user is typing
      // this.showStripeKey = value.length > 0;
    }
  }
};
</script>
